/**
 * Priority system ported from legacy/erp.js
 *
 * Priority codes (higher = more important, displayed first):
 * - entered (4096): Yellow - employee is at client (ACTIVE status)
 * - urgent (2048): Yellow - urgent visit
 * - deadline (1024): LightSalmon - past deadline
 * - today (512): PowderBlue - scheduled day = today
 * - visit (256): GreenYellow - pending visit
 * - personal (128): Aquamarine - personal task
 * - outdoor (64): AntiqueWhite - outdoor task
 * - regular (2): Default - regular client
 * - been (1): Strikethrough gray - already visited
 */

export const PRIORITY_CODES = {
  entered: 4096,
  urgent: 2048,
  deadline: 1024,
  today: 512,
  visit: 256,
  personal: 128,
  outdoor: 64,
  regular: 2,
  been: 1,
} as const;

export type PriorityType = keyof typeof PRIORITY_CODES;

// Tailwind classes for each priority
export const PRIORITY_STYLES: Record<PriorityType, {
  bg: string;
  border: string;
  text?: string;
  strikethrough?: boolean;
}> = {
  entered: {
    bg: 'bg-sky-200',
    border: 'border-l-4 border-l-sky-500',
    text: 'text-gray-900',
  },
  urgent: {
    bg: 'bg-amber-200',
    border: 'border-l-4 border-l-amber-500',
    text: 'text-gray-900',
  },
  deadline: {
    bg: 'bg-[#FFA07A]', // LightSalmon
    border: 'border-l-4 border-l-red-500',
    text: 'text-gray-900',
  },
  today: {
    bg: 'bg-[#B0E0E6]', // PowderBlue
    border: 'border-l-4 border-l-blue-400',
    text: 'text-gray-900',
  },
  visit: {
    bg: 'bg-[#ADFF2F]', // GreenYellow
    border: 'border-l-4 border-l-green-500',
    text: 'text-gray-900',
  },
  personal: {
    bg: 'bg-[#7FFFD4]', // Aquamarine
    border: 'border-l-4 border-l-cyan-500',
    text: 'text-gray-900',
  },
  outdoor: {
    bg: 'bg-[#FAEBD7]', // AntiqueWhite
    border: 'border-l-4 border-l-amber-500',
    text: 'text-gray-900',
  },
  regular: {
    bg: 'bg-card',
    border: 'border-l-4 border-l-border',
  },
  been: {
    bg: 'bg-muted',
    border: 'border-l-4 border-l-border',
    text: 'text-muted-foreground',
    strikethrough: true,
  },
};

export interface VisitPriorityParams {
  isActive: boolean;           // Currently checked in (ACTIVE status)
  isUrgent: boolean;           // URGENT priority
  hasDeadline: boolean;        // Deadline <= today
  isPreferredDayToday: boolean; // Today is preferred day
  hasPendingVisit: boolean;    // Has pending visit
  isVisited: boolean;          // Already visited
  isSkipped: boolean;          // Skipped
  hasOutdoorTask: boolean;     // Has OUTDOOR task
}

/**
 * Calculate priority type for a visit item
 * Replicates getTaskCode() from legacy/erp.js
 */
export function getVisitPriority(params: VisitPriorityParams): PriorityType {
  // Already completed - show at bottom
  if (params.isVisited || params.isSkipped) {
    return 'been';
  }

  // Active visit - highest priority
  if (params.isActive) {
    return 'entered';
  }

  // Urgent - high priority
  if (params.isUrgent) {
    return 'urgent';
  }

  // Past deadline
  if (params.hasDeadline) {
    return 'deadline';
  }

  // Today is preferred day
  if (params.isPreferredDayToday) {
    return 'today';
  }

  // Has pending visit scheduled
  if (params.hasPendingVisit) {
    return 'visit';
  }

  // Has outdoor task
  if (params.hasOutdoorTask) {
    return 'outdoor';
  }

  return 'regular';
}

export interface TaskPriorityParams {
  type: 'OUTDOOR' | 'PERSONAL' | 'INDOOR';
  hasDeadline: boolean;
  isCompleted: boolean;
  isCancelled: boolean;
}

/**
 * Calculate priority type for a task (for sorting)
 */
export function getTaskPriority(params: TaskPriorityParams): PriorityType {
  if (params.isCompleted || params.isCancelled) {
    return 'been';
  }

  if (params.hasDeadline) {
    return 'deadline';
  }

  if (params.type === 'PERSONAL') {
    return 'personal';
  }

  if (params.type === 'OUTDOOR') {
    return 'outdoor';
  }

  return 'regular';
}

/**
 * Get deadline status for a task
 */
export function getDeadlineStatus(deadline: string | null | undefined): 'overdue' | 'today' | 'thisWeek' | null {
  if (!deadline) return null;

  // Parse dates as local dates (YYYY-MM-DD format)
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  // Extract just the date part from deadline (handles both "2024-12-28" and "2024-12-28T00:00:00.000Z")
  const deadlineStr = deadline.split('T')[0];

  // Compare as strings (YYYY-MM-DD format compares correctly)
  if (deadlineStr < todayStr) {
    return 'overdue';
  }

  if (deadlineStr === todayStr) {
    return 'today';
  }

  // Check if within next 7 days
  const weekEnd = new Date(today);
  weekEnd.setDate(weekEnd.getDate() + 7);
  const weekEndStr = `${weekEnd.getFullYear()}-${String(weekEnd.getMonth() + 1).padStart(2, '0')}-${String(weekEnd.getDate()).padStart(2, '0')}`;

  if (deadlineStr < weekEndStr) {
    return 'thisWeek';
  }

  return null;
}

/**
 * Task-specific styles based on deadline (separate from visit priority system)
 * - overdue/today: salmon (like deadline in visits)
 * - thisWeek: yellow
 * - null: use default task type colors
 */
export const TASK_DEADLINE_STYLES = {
  overdue: {
    bg: 'bg-[#FFA07A]', // LightSalmon
    border: 'border-l-4 border-l-red-500',
    text: 'text-gray-900',
  },
  today: {
    bg: 'bg-[#FFA07A]', // LightSalmon
    border: 'border-l-4 border-l-red-500',
    text: 'text-gray-900',
  },
  thisWeek: {
    bg: 'bg-yellow-200',
    border: 'border-l-4 border-l-yellow-500',
    text: 'text-gray-900',
  },
} as const;

/**
 * Get style for a task based on its deadline and type
 */
export function getTaskStyle(params: {
  type: 'OUTDOOR' | 'PERSONAL' | 'INDOOR';
  deadline: string | null | undefined;
  isCompleted: boolean;
  isCancelled: boolean;
}): { bg: string; border: string; text?: string; strikethrough?: boolean } {
  // Completed/cancelled - gray
  if (params.isCompleted || params.isCancelled) {
    return PRIORITY_STYLES.been;
  }

  // Check deadline status
  const deadlineStatus = getDeadlineStatus(params.deadline);
  if (deadlineStatus && TASK_DEADLINE_STYLES[deadlineStatus]) {
    return TASK_DEADLINE_STYLES[deadlineStatus];
  }

  // Default: use type-based colors
  if (params.type === 'PERSONAL') {
    return PRIORITY_STYLES.personal;
  }
  if (params.type === 'OUTDOOR') {
    return PRIORITY_STYLES.outdoor;
  }

  return PRIORITY_STYLES.regular;
}

/**
 * Get numeric priority code for sorting
 */
export function getPriorityCode(priority: PriorityType): number {
  return PRIORITY_CODES[priority];
}

/**
 * Convert numeric code to priority type
 */
export function codeToColor(code: number): PriorityType {
  const entries = Object.entries(PRIORITY_CODES) as [PriorityType, number][];

  // Find the highest matching priority
  for (const [type, value] of entries.sort((a, b) => b[1] - a[1])) {
    if (code >= value) {
      return type;
    }
  }

  return 'regular';
}

/**
 * Get combined Tailwind classes for a priority
 */
export function getPriorityClasses(priority: PriorityType): string {
  const style = PRIORITY_STYLES[priority];
  const classes = [style.bg, style.border];

  if (style.text) {
    classes.push(style.text);
  }

  if (style.strikethrough) {
    classes.push('line-through');
  }

  return classes.join(' ');
}
